/**
 * NotificationContext
 * FCM onMessage → 커스텀 이벤트 → Context 상태 관리
 * 앱 어디서든 useNotification()으로 알림 목록/뱃지/읽음 접근
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

const MAX_NOTIFICATIONS = 50;
const EVENT_NAME = "fcm-message";

export interface AppNotification {
  id: string;
  type: string;
  boardType?: string;
  targetId?: string;
  nickName?: string;
  source?: string;
  title: string;
  body: string;
  // 게시글이 있는 타입(개선요청/오류신고/관리자게시판/요구사항게시판 등)만 실려옴 —
  // 1:1문의/배너댓글/배너단톡방/광고문의처럼 게시글 개념이 없는 타입은 빈 문자열(댓글내용 사용)
  postTitle?: string;
  timestamp: number;
  read: boolean;
}

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markReadForTarget: (targetId: string) => void;
  clear: () => void;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  markAsRead: () => {},
  markReadForTarget: () => {},
  clear: () => {},
});

export function useNotification() {
  return useContext(NotificationContext);
}

/** firebase.ts에서 호출 — React 외부에서 Context로 알림 전달 */
export function dispatchFcmEvent(detail: {
  type: string;
  boardType?: string;
  targetId?: string;
  nickName?: string;
  source?: string;
  title: string;
  body: string;
  postTitle?: string;
}) {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
}

const STORAGE_KEY = "trippang_notifications";
// 일회성 알림 wipe 플래그 — 서버 측 알림 일괄 삭제 시점에 클라이언트도 비우도록
const WIPE_FLAG_KEY = "trippang_notifications_wipe_20260425";

// 며칠씩 지난 FCM 알림이 계속 목록에 쌓여 남아있으면 의미 없음(이미 처리됐거나 관심 밖) →
// 일정 시간 지나면 자동으로 목록에서 사라지도록 만료 처리 (어드민에서 겪은 동일 문제 적용).
const NOTIF_EXPIRY_MS = 48 * 60 * 60 * 1000; // 48시간
const notExpired = (n: AppNotification) => Date.now() - n.timestamp < NOTIF_EXPIRY_MS;

function loadNotifications(): AppNotification[] {
  try {
    if (!localStorage.getItem(WIPE_FLAG_KEY)) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(WIPE_FLAG_KEY, "1");
      return [];
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    const list: AppNotification[] = raw ? JSON.parse(raw) : [];
    return list.filter(notExpired);
  } catch { return []; }
}

function saveNotifications(list: AppNotification[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_NOTIFICATIONS))); } catch {}
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>(loadNotifications);

  const handleFcmEvent = useCallback((e: Event) => {
    const { type, boardType, targetId, nickName, source, title, body, postTitle } = (e as CustomEvent).detail;
    const newNotif: AppNotification = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      boardType,
      targetId,
      nickName,
      source,
      title,
      body,
      postTitle,
      timestamp: Date.now(),
      read: false,
    };
    setNotifications((prev) => {
      const next = [newNotif, ...prev.filter(notExpired)].slice(0, MAX_NOTIFICATIONS);
      saveNotifications(next);
      return next;
    });
  }, []);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, handleFcmEvent);

    // Service Worker postMessage 수신 (Safari 포그라운드 대응)
    const handleSwMessage = (e: MessageEvent) => {
      if (e.data?.type === "FCM_MESSAGE") {
        const { title, body, data } = e.data.payload;
        // 본인이 보낸 메시지는 무시
        const senderId = data?.sender_id;
        const myId = localStorage.getItem("user_id");
        if (senderId && myId && String(senderId) === String(myId)) return;
        window.dispatchEvent(new CustomEvent(EVENT_NAME, {
          detail: {
            type: data?.type || "",
            boardType: data?.board_type || "",
            targetId: data?.target_id || "",
            nickName: data?.nick_name || "",
            source: data?.source || "",
            title: title || "",
            body: body || "",
            postTitle: data?.post_title || "",
          },
        }));
      }
    };
    navigator.serviceWorker?.addEventListener("message", handleSwMessage);

    return () => {
      window.removeEventListener(EVENT_NAME, handleFcmEvent);
      navigator.serviceWorker?.removeEventListener("message", handleSwMessage);
    };
  }, [handleFcmEvent]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) => {
      const next = prev.map((n) => n.id === id ? { ...n, read: true } : n);
      saveNotifications(next);
      return next;
    });
  }, []);

  // 베너 상세(댓글/1:1문의 등)를 종 드롭다운을 거치지 않고 직접 연 경우(시스템 알림 클릭,
  // 배너 그리드 클릭, 공유 링크 등) — 그 베너에 대한 알림들도 함께 읽음 처리.
  // 안 그러면 화면은 이미 봤는데 종 뱃지 숫자만 그대로 남는 문제가 있었음.
  const markReadForTarget = useCallback((targetId: string) => {
    if (!targetId) return;
    setNotifications((prev) => {
      let changed = false;
      const next = prev.map((n) => {
        if (!n.read && n.targetId === targetId) { changed = true; return { ...n, read: true }; }
        return n;
      });
      if (!changed) return prev;
      saveNotifications(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setNotifications([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, markReadForTarget, clear }}>
      {children}
    </NotificationContext.Provider>
  );
}
